import React, { useState, useEffect, useRef } from "react";
import axios from "api/axios";
import { Button, NoStyleButton, OutlineButton, SubtleButton } from "components/button";
import { Input } from "components/input";
import MultiInput from "components/input/multi";
import { useModalContext } from "components/modal";
import ReactSelect from "components/react-select";
import { SContent } from "components/segment";
import { Overline } from "components/typography/overline";
import { useFormik } from "formik";
import { cloneDeep, set } from "lodash";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { theme } from "styles/theme";
import { TEstimateCD } from "types/custom-doc";
import { TQuoteSubmission } from "types/project";
import { dollarFormatter } from "utils";
import { EstimateCustomDocumentContent } from "views/custom_document";
import Select from "react-select";
import { TEstimateOptions } from "types/estimate";
import { useSnackbarContext } from "components/snackbar";
import CustomDocModal from "containers/customDocModal";
import AdditionalTerms from "containers/AdditionalTerms";
import Assignee from "components/react-select/assignees";
import Field from "components/field";
import TooltippedElement from "components/TooltippedElement";
import { FaInfoCircle } from "react-icons/fa";
import { MdAttachFile } from "react-icons/md";
import { generatePdfThumbnail } from "components/pdf-thumbnail";
import FileGrid from "components/file-grid";
import { useTranslation } from 'react-i18next';
import { TProperty } from "types/property";
import { activeAdditionalTermsForPayload } from "utils/additionalTermsPayload";
import { normalizeCustomTextForPayload } from "utils/customTextPayload";

type Props = {
  projectId: string;
};

type TOption = { id: number; label: string; total: number; selected?: boolean };
type SubmitQuoteData = Omit<TQuoteSubmission, "options"> & {
  options: TOption[];
};

export default function SubmitQuoteModal({ projectId }: Props) {
  const { t } = useTranslation();
  const tBase = "views.projects.views.project.views.quote.components.submitQuoteModal";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { setModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();
  const queryClient = useQueryClient();
  const history = useHistory();

  const [step, setStep] = useState(0);
  const [data, setData] = useState<SubmitQuoteData[] | null>(null);
  const [templateId, setTemplateId] = useState<string>();
  const [customText, setCustomText] = useState<string>("");
  const [manager, setManager] = useState<{
      value: string;
      label: string;
    } | null>(null);
  const [noColor, setNoColor] = useState<boolean>(false);
  const [additionalTerms, setAdditionalTerms] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [billingAddresses, setBillingAddresses] = useState<TProperty[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);


  const inputRef = useRef<HTMLInputElement>(null);

  const query = useQuery<{
    info: SubmitQuoteData[];
    manager: { value: string; label: string } | null;
  }>(
    ["submit quote"],
    async () =>
      await axios
        .get(
          `
    ${process.env.REACT_APP_SERVER_URL}/api/v1/company/projects/${projectId}/submit-quote
    `
        )
        .then((res) => res.data),
    {
      onSuccess: (res) => {
        setData(res.info);
        setManager(res.manager);
      },
      refetchOnWindowFocus: false,
    }
  );

  const mutation = useMutation(
    async () => {
      const customTextPayload = normalizeCustomTextForPayload(customText);
      const formData = new FormData();

      attachments.forEach((file) => {
        if (file.new) { // new file
          if (file.editedName) {
            const newFile = new File([file.fullFile], file.editedName, { type: file.fullFile.type });
            const newThumbnail = new File([file.thumbnailFile], file.editedName, { type: file.thumbnailFile.type });
        
            formData.append("attachments", newFile);
            formData.append("thumbnails", newThumbnail);
          } else {
            formData.append("attachments", file.fullFile); 
            formData.append("thumbnails", file.thumbnailFile); 
          }
        } else { // fetched from request/project
          formData.append("fetchedFiles", JSON.stringify({ 
            name: file.name,
            editedName: file.editedName || "",
            key: file.name, 
            url: file.url,
            type: "project",
            typeId: projectId,
          }));
        }
      });
      
      formData.append("templateId", templateId || "");
      // formData.append("emails", JSON.stringify(emails));
      formData.append("customText", customTextPayload);
      formData.append("fk_manager_id", manager?.value || "");
      formData.append("noColor", noColor.toString());
      formData.append(
        "additionalTerms",
        JSON.stringify(activeAdditionalTermsForPayload(additionalTerms))
      );
      formData.append("projectId", projectId);
      formData.append("data", JSON.stringify(data));
      formData.append("billingAddresses", JSON.stringify(billingAddresses));

      return await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v2/company/quotes/${projectId}/submit`,
        formData
        // {
        //   data,
        //   templateId,
        //   fk_manager_id: manager?.value,
        // }
      );
    },
    {
      onSuccess: () => {
        setModal();
        showSnackbar(tr("Quote sent!"));
        history.push(`/projects/${projectId}`);
        queryClient.invalidateQueries();
      },
      onError: () => {
        showSnackbar(tr("Error sending quote"));
      },
    }
  );

  const preview = useMutation(
    async () =>
      await axios
        .post(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quotes/${projectId}/preview`,
          {
            templateId,
            customText: normalizeCustomTextForPayload(customText),
            additionalTerms: activeAdditionalTermsForPayload(additionalTerms),
            noColor,
            previewData,
            billingAddress: billingAddresses.find((a) => a.fk_bcustomer_id === previewData.id),
          }
        )
        .then((res) => res.data),
    {
      onSuccess: (url) => {
        window.open(url, "_blank");
      },
      onError: () => {
        showSnackbar(tr("Error previewing quote"));
      },
    }
  );

  const { refetch: refetchFiles } = useQuery(["requestOrProjectFiles"],
    async () => {
      return await axios
        .get(`
          ${process.env.REACT_APP_SERVER_URL}/api/v1/company/files/project/${projectId}
        `)
        .then((res) => res.data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: false
    }
  );

  const attachmentUpload = () => {
    inputRef.current?.click();
  };

  // handles new files
  const handleFileSelection = (files: FileList) => {
    try {
      setAttachments((prev) => {
        const validExtensions = [".pdf", ".png", ".jpg", ".jpeg"];
        const uniqueFiles = Array.from(files).filter((file) => {
          const fileExtension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
          const isValidExtension = validExtensions.includes(fileExtension);
          const isUnique = !prev.some((existingFile) => existingFile.name === file.name);

          if (!isValidExtension) {
            showSnackbar(tr("Invalid file type, supported types are") + ": PDF, PNG, JPG, JPEG");
          } else if (!isUnique) {
            showSnackbar(tr("File already added"));
          }

          return isValidExtension && isUnique;
        });

        const processedFiles = uniqueFiles.map(async (file) => {
          if (file.type.startsWith("image/")) {
            return {
              name: file.name,
              type: file.type,
              url: URL.createObjectURL(file), // For images, use a preview URL
              thumbnail: URL.createObjectURL(file),
              thumbnailFile: file,
              new: true,
              fullFile: file,
            };
          } else if (file.type === "application/pdf") {
            const thumbnail = await generatePdfThumbnail(file);
            const thumbnailBlob = thumbnail && await fetch(thumbnail).then((res) => res.blob());
            const thumbnailFile = thumbnailBlob && new File([thumbnailBlob], file.name, { type: "image/png" });
            return {
              name: file.name,
              type: file.type,
              url: URL.createObjectURL(file), // Thumbnail for PDF
              thumbnail: thumbnail, // Preview URL for PDF
              thumbnailFile: thumbnailFile,
              new: true,
              fullFile: file,
              isPdf: true,
            };
          } else {
            return {
              name: file.name,
              type: file.type,
              url: null, // Unsupported file types
              thumbnail: null,
              new: true,
              fullFile: file,
            };
          }
        });
    
        // Wait for all thumbnail generation to complete
        Promise.all(processedFiles).then((finalFiles) => {
          setAttachments([...prev, ...finalFiles]);
        });
    
        return prev;
      });
    } catch (e) {
      showSnackbar(tr("Error adding file(s)"));
    }
  };

  const addFiles = async () => {
    try {
      const { data: fetchedFiles } = await refetchFiles(); 
      if (fetchedFiles) {
        setAttachments((prev) => {
          const uniqueFiles = fetchedFiles
            .filter((file: any) => file.signedUrl) 
            .map((file: any) => ({
              name: file.Key, 
              url: file.signedUrl, 
              type: file.type, 
              size: file.Size, 
              lastModified: file.LastModified, 
            }))
            .filter((file: any) => 
              !prev.some((existingFile) => existingFile.name === file.name) // Prevent duplicates
            );
  
          return [...prev, ...uniqueFiles];
        });
      }
    } catch (error) {
      showSnackbar(tr("Error adding file(s)"));
      console.error("Error fetching files:", error);
    }
  };

  if (!data) return null;

  const last = step === data.length - 1;

  const isFinalizing = step === data.length;

  const next = () => {
    setStep((s) => s + 1);
  };

  const back = () => {
    setStep((s) => s - 1);
    setShowPreview(false);
    setPreviewData(null);
  };

  const optionClick = (id: number) => {
    setData((data) => {
      let temp = cloneDeep(data)!;

      let optionIndex = temp[step].options.findIndex(
        (option) => option.id === id
      );
      let option = temp[step].options[optionIndex];

      if (option.selected) {
        option.selected = false;
      } else {
        option.selected = true;
      }

      return temp;
    });
  };

  const emailChange = (emails: (string | null)[]) => {
    setData((data) => {
      let temp = cloneDeep(data)!;

      temp[step].emails = emails;
      return temp;
    });
  };

  const handleSkip = () => {
    setStep((s) => s + 1);
  };

  const handleTermEdit = (id: string, updatedTerm: { text?: string; active?: boolean, type?: string, order?: number }) => {
    setAdditionalTerms((prev) =>
      prev.map((term) =>
        term.id === id
          ? { ...term, ...updatedTerm } // Update both text and active properties
          : term
      )
    );
  };

  const handleTermsLoad = (terms: any[]) => {
    setAdditionalTerms((prev) =>
      terms.map((term) => {
        // Retain edits or use the loaded data
        const existingTerm = prev.find((t) => t.id === term.id);
        return {
          ...term,
          active: existingTerm?.active ?? term.active ?? true,
          text: existingTerm?.text ?? term.text,
          type: existingTerm?.type ?? term.type,
          order: existingTerm?.order ?? term.order
        };
      })
    );
  };

  const handleEditStatusChange = (isEditing: boolean) => {
    setIsEditing(isEditing);
  };

  return (
    <div className="max-w-[500px] space-y-4 mr-2">
      {isFinalizing ? (
        // <EstimateCustomDocumentContent formik={formik} />
        <>
          <CustomDocModal
            id={templateId}
            type="estimate"
            onClick={(e) => setTemplateId((id) => (id === e ? undefined : e))}
          />
          <AdditionalTerms
            id={templateId}
            onEdit={(id, updatedTerm) => handleTermEdit(id, updatedTerm)} // Pass the updated term object
            onLoad={handleTermsLoad}
            onEditStatusChange={handleEditStatusChange}
          />
          <Field
            label={tr("Custom Text")}
            type="textarea"
            minRows={4}
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
          />
          <Assignee
            label={tr("Manager")}
            value={manager}
            isMulti={false}
            onChange={(e: any) => setManager(e)}
          />
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={noColor}
              onChange={() => setNoColor(!noColor)}
            />
            <label className="ml-2">{tr("Black & White Print")}</label>
            <TooltippedElement
              message={tr("Turn Off Color for Printing PDF")}
              placement="top"
              element={<FaInfoCircle className="ml-1 text-secondary" />}
            />
          </div>
          {/* Attachments */}
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[12.8px]">{tr("Attachments")}:</div>
              {attachments.length > 0 && (
                <div>
                  <button
                    className="text-xs inline-block text-red-400 underline hover:no-underline"
                    onClick={() => setAttachments([])}
                    type="button"
                  >
                    {tr("Remove All Attachments")}
                  </button>
                </div>
              )}
            </div>
            {attachments.length > 0 ? (
              <FileGrid
                id={projectId}
                type="project"
                files={attachments}
                setFiles={setAttachments}
                refetch={refetchFiles}
                isLocal={true}
              />
            ) : (
              <div className="text-gray-500">{tr("No attachments")}</div>
            )}
          </div>
        </>
      ) : (
        <EachContractor
          {...{ step, optionClick, emailChange }}
          length={data.length}
          data={data[step]}
          billingAddresses={billingAddresses}
          setBillingAddresses={setBillingAddresses}
        />
      )}
      {showPreview && (
        <div className="space-y-2 w-full bg-blue-50 border rounded-md p-4">
          <Overline>{tr("Pick Contractor to Preview")}</Overline>
          <ReactSelect
            className="w-full bg-white"
            isMulti={false} 
            options={data
              .filter(contractor => {
                // Only include contractors that have selected options and billing address
                const hasSelectedOptions = contractor.options.some(opt => opt.selected);
                const hasBillingAddress = billingAddresses.find(b => b.fk_bcustomer_id === contractor.id);
                return hasSelectedOptions && hasBillingAddress;
              })
              .map(c => ({ value: c.id, label: c.name }))}
            value={previewData ? { value: previewData.id, label: previewData.name } : null}
            onChange={selected => {
              if (selected) {
                const selectedContractor = data.find(c => c.id === selected.value);
                if (selectedContractor) {
                  // Create preview data with only selected options
                  const previewContractor = {
                    ...selectedContractor,
                    options: selectedContractor.options.filter(opt => opt.selected)
                  };
                  setPreviewData(previewContractor);
                }
              } else {
                setPreviewData(null);
              }
            }}
            isClearable
            placeholder={tr("None")}
          />
          <div className="flex justify-end space-x-2 mt-2">
            <Button onClick={() => setShowPreview(false)}>
              {tr("Cancel")}
            </Button>
            <Button
              primary
              isLoading={preview.isLoading}
              disabled={!templateId || !manager || preview.isLoading || isEditing || !previewData}
              onClick={() => {
                if (!showPreview) {
                  setShowPreview(true);
                } else {
                  preview.mutate();
                }
              }}
            >
              {tr("Preview")}
            </Button>
          </div>
        </div>
      )}
      <div className={`flex justify-end items-end space-x-2 ${showPreview ? "mt-0" : "mt-4"}`}>
        {step !== 0 && (
          <OutlineButton onClick={back}>
            {tr("Back")}
          </OutlineButton>
        )}
        {!isFinalizing && (
          <div className="space-x-2">
            <OutlineButton onClick={handleSkip}>
              {tr("Skip")}
            </OutlineButton>
            <OutlineButton onClick={() => setModal()}>
              {tr("Cancel")}
            </OutlineButton>
          </div>
        )}
        {isFinalizing && (
          <>
            <TooltippedElement
              message={tr("Add files from project")}
              placement="top"
              element={
                <Button onClick={() => addFiles()} className="relative">
                  <MdAttachFile />
                  <div className="absolute top-0 right-2 text-[16px]">+</div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[8px]">
                    {tr("Project")}
                  </div>
                </Button>
              }
            />
            <Button onClick={attachmentUpload}>
              <MdAttachFile />
            </Button>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg"
              onClick={(e) => {
                (e.target as HTMLInputElement).value = ""; // Reset file input
              }}
              onChange={(e) => {
                if (e.target.files) {
                  handleFileSelection(e.target.files);
                }
              }}
              className="hidden"
            />
            <SubtleButton
              disabled={!templateId || !manager || preview.isLoading || isEditing || showPreview}
              primary
              onClick={() => setShowPreview(true)}
            >
              {tr("Preview")}
            </SubtleButton>

          </>
        )}

        <Button
          onClick={() => (isFinalizing ? mutation.mutate() : next())}
          primary
          isLoading={mutation.isLoading}
          disabled={
            (!isFinalizing &&
              // data[step].emails.length === 0 ||
              (data[step].options.filter((o) => o.selected).length === 0 ||
              !billingAddresses.find((b) => b.fk_bcustomer_id === data[step].id))) ||
            (isFinalizing && (!templateId || !manager))
          }
        >
          {last ? tr("Finalize") : isFinalizing ? tr("Submit") : tr("Next")}
        </Button>
      </div>
    </div>
  );
}


type ECProps = {
  data: SubmitQuoteData;
  length: number;
  step: number;
  optionClick: (id: number) => void;
  emailChange: (email: (string | null)[]) => void;
  billingAddresses: TProperty[];
  setBillingAddresses: (addresses: TProperty[]) => void;
};

export const EachContractor = (props: ECProps) => {
  const { t } = useTranslation();
  const tBase = "views.projects.views.project.views.quote.components.submitQuoteModal";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const [billing, setBilling] = useState<TProperty>();
  const [showAll, setShowAll] = useState(false);

  const customerId = props.data.id;

  useEffect(() => {
    const savedAddress = props.billingAddresses.find((a) => a.fk_bcustomer_id === customerId);
    if (savedAddress) {
      setBilling(savedAddress);
    }
  }, [customerId, props.billingAddresses]);

  const populateOptions = props.data.options
    .sort((a, b) => a.label.localeCompare(b.label)) // order options alphabetically
    .map((option) => {
    return (
      <EachOption
        id={option.id}
        selected={option.selected}
        total={option.total}
        label={option.label}
        optionClick={props.optionClick}
        key={option.id}
      />
    );
  });

  const billingAddress = useQuery<TProperty[] | null>(
    ["billing address", customerId],
    async () =>
      await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/customers/${customerId}/billing_address`)
        .then((res) => res.data),
    {
      enabled: !!customerId,
    }
  );

  const visibleAddresses = showAll ? billingAddress.data : billingAddress.data?.slice(0, 5);

  const populateBillingAddresses = visibleAddresses?.map((address) => {
    const fullAddress = `${address.address} ${address.city}, ${address.state} ${address.zipcode}${address.zip_ext ? `-${address.zip_ext}` : ""}`;
    return (
      <label key={address.id} className="flex items-center space-x-2 space-y-2">
        <input
          type="radio"
          value={address.id}
          name="billing_address"
          onChange={() => {
            setBilling(address);
            props.setBillingAddresses([
              ...props.billingAddresses.filter((a) => a.fk_bcustomer_id !== customerId),
              { ...address },
            ]);
          }}
          checked={billing?.id === address.id}
          className="mt-2"
        />
        <div>
          <div className="text-wrap">
            {fullAddress}
            {address.name && <span className="text-gray-500 italic"> ({address.name})</span>}
          </div>
        </div>
      </label>
    );
  });
  
  return (
    <SEachContractor>
      <Overline>
        {tr("Contractor")} {props.step + 1} {tr("of")} {props.length}
      </Overline>

      <h2 style={{ marginBottom: "0.5rem" }}>{props.data.name}</h2>
      {/* <Input label="Estimate Title" /> */}
      <Overline>{tr("Email")}</Overline>
      <MultiInput
        values={props.data.emails}
        onChange={props.emailChange}
        error={props.data.emails.length === 0}
      />
      <div className="my-2">
        <Overline>{tr("Billing Address")}</Overline>
        <div className="flex flex-col">{populateBillingAddresses}</div>
      </div>
      {billingAddress && (billingAddress.data ?? []).length > 5 && (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className="text-blue-600 hover:underline text-sm"
        >
          {showAll ? tr("Show Less") : tr("Show More")}
        </button>
      )}
      <Overline>{tr("Options")}</Overline>
      {populateOptions}
    </SEachContractor>
  );
};

const SEachContractor = styled.div`
  display: flex;
  flex-direction: column;
`;

const SOption = styled(NoStyleButton)<{ selected?: boolean }>`
  border: 1px solid
    ${(props) => (props.selected ? theme.color.main : theme.border.light)};
  background: ${(props) =>
    props.selected ? theme.bg.lightorange : "transparent"};
  padding: 0.5rem;
  margin: 0.5rem 0;
`;

export const EachOption = ({
  label,
  total,
  id,
  selected,
  optionClick,
}: {
  label: string;
  total: number;
  id: number;
  selected?: boolean;
  // option: TOption | TEstimateOptions;
  optionClick: (id: number) => void;
}) => {
  return (
    <SOption key={id} onClick={() => optionClick(id)} selected={selected}>
      <h3>{label}</h3>
      <h4 style={{ color: theme.color.subtext3 }}>{dollarFormatter(total)}</h4>
    </SOption>
  );
};
